/**
 * iter-312p: Manage Credit Notes for a project.
 *
 * Small modal used inside Edit BOQ → Actual Summary → "Manage credit notes".
 * Lists, adds, and deletes credit notes against a project. The Edit BOQ's
 * "Credit Notes Applied" row auto-picks up the new sum on next refresh.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Trash2, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CreditNotesDialog({ open, onOpenChange, projectId, projectName, onChange }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/credit-notes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setNotes(r.data.notes || []);
    } catch {
      toast.error('Failed to load credit notes');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  const total = notes.reduce((s, n) => s + (+n.amount || 0), 0);

  const handleAdd = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/projects/${projectId}/credit-notes`, { amount: amt, reason: reason.trim(), date }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Credit note added');
      setAmount(''); setReason('');
      await reload();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add credit note');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this credit note?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/credit-notes/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Credit note deleted');
      await reload();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // iter-315e: Apply / Un-apply a Credit Note. Apply records a transaction in
  // Project Account ledger and the Payment Receivable picks it up via the
  // applicable-payments / client-payments-summary endpoint.
  const [applyingId, setApplyingId] = React.useState(null);
  const handleApply = async (n) => {
    if (!window.confirm(`Apply credit note of ₹${Math.round(+n.amount).toLocaleString()} to Payment Receivable?\n\nThis will record a transaction in the Project Accounts ledger.`)) return;
    setApplyingId(n.id);
    try {
      await axios.post(`${API}/projects/${projectId}/credit-notes/${n.id}/apply`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Credit note applied');
      await reload();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplyingId(null);
    }
  };
  const handleUnapply = async (n) => {
    if (!window.confirm(`Un-apply this credit note? The ledger transaction will be removed.`)) return;
    setApplyingId(n.id);
    try {
      await axios.post(`${API}/projects/${projectId}/credit-notes/${n.id}/unapply`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Credit note un-applied');
      await reload();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to un-apply');
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl !max-h-[88vh] flex flex-col overflow-hidden p-0"
        style={{ zoom: 1.25 }}
        data-testid="credit-notes-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-stone-200 bg-white flex-shrink-0">
          <DialogTitle>Credit Notes — {projectName || 'Project'}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
          {/* Add form */}
          <div className="rounded-md border border-stone-200 p-3 bg-stone-50">
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-3">
                <Label className="text-xs">Amount (₹) *</Label>
                <Input type="number" min="0" step="1" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 5000" data-testid="cn-amount" />
              </div>
              <div className="col-span-3">
                <Label className="text-xs">Date</Label>
                <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="cn-date" />
              </div>
              <div className="col-span-4">
                <Label className="text-xs">Reason *</Label>
                <Textarea rows={1} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Partial refund for damaged material" data-testid="cn-reason" />
              </div>
              <div className="col-span-2">
                <Button onClick={handleAdd} disabled={creating} className="w-full" data-testid="cn-add-btn">
                  <Plus size={14} className="mr-1" />{creating ? 'Adding…' : 'Add'}
                </Button>
              </div>
            </div>
          </div>

          {/* List */}
          <div className="rounded-md border border-stone-200 overflow-hidden">
            <div className="bg-stone-50 px-3 py-2 flex items-center justify-between text-sm">
              <span className="font-semibold text-stone-700">Credit notes ({notes.length})</span>
              <span className="text-stone-600">Total: <strong>₹{Math.round(total).toLocaleString()}</strong></span>
            </div>
            {loading ? (
              <div className="p-6 text-center text-stone-400 text-sm">Loading…</div>
            ) : notes.length === 0 ? (
              <div className="p-6 text-center text-stone-400 text-sm">No credit notes yet.</div>
            ) : (
              <div className="max-h-80 overflow-y-auto divide-y divide-stone-100">
                {notes.map(n => (
                  <div key={n.id} className="px-3 py-2 flex items-start justify-between gap-3 hover:bg-stone-50">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs text-stone-500 flex-wrap">
                        <span>{n.date}</span>
                        <span>·</span>
                        <span>{n.created_by_name}</span>
                        {n.applied_at && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold uppercase tracking-wide text-[9px]">
                            ✓ Applied
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-stone-800 whitespace-pre-wrap break-words">{n.reason}</div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-1">
                      <span className="text-sm font-bold text-red-600">−₹{Math.round(+n.amount).toLocaleString()}</span>
                      <div className="flex items-center gap-1">
                        {n.applied_at ? (
                          <button onClick={() => handleUnapply(n)} disabled={applyingId === n.id} className="text-xs px-2 py-0.5 rounded border border-stone-300 text-stone-600 hover:bg-stone-100" title="Un-apply" data-testid={`cn-unapply-${n.id}`}>
                            {applyingId === n.id ? '…' : 'Un-apply'}
                          </button>
                        ) : (
                          <button onClick={() => handleApply(n)} disabled={applyingId === n.id} className="text-xs px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700" title="Apply to Payment Receivable" data-testid={`cn-apply-${n.id}`}>
                            {applyingId === n.id ? '…' : 'Apply'}
                          </button>
                        )}
                        {!n.applied_at && (
                          <button onClick={() => handleDelete(n.id)} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Delete" data-testid={`cn-delete-${n.id}`}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
